// src/components/Organizations/Marketing/OrganizationMarketingTab.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Grid,
  Button,
  Divider,
  Alert,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CardMedia,
  CardActions
} from '@mui/material';
import {
  Campaign as MarketingIcon,
  Image as ImageIcon,
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  CloudUpload as UploadIcon,
  Addchart as AnalyticsIcon,
  Chat as ChatIcon,
  Campaign as CampaignIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import { useOrganization } from '../../../contexts/OrganizationContext';
import OrganizationLogoManager from './OrganizationLogoManager';
import SocialMediaCampaignCreator from './SocialMediaCampaignCreator';
import CampaignsTable from './CampaignsTable';
import { addDoc, getDocs, deleteDoc, query, where } from 'firebase/firestore';
import { collection, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import AIImageGenerator from '../../../components/EventForms/AIImageGenerator';

interface OrganizationMarketingTabProps {
  eventId: string;
  organizationId: string;
  eventTitle: string;
  eventDescription: string;
  eventType: string;
  eventDate: Date;
  eventLocation: string;
}

const OrganizationMarketingTab: React.FC<OrganizationMarketingTabProps> = ({
  eventId,
  organizationId,
  eventTitle,
  eventDescription,
  eventType,
  eventDate,
  eventLocation
}) => {
  const { activeOrganization } = useOrganization();
  const [activeSection, setActiveSection] = useState<number>(0);
  const [logoManagerOpen, setLogoManagerOpen] = useState(false);
  const [campaignCreatorOpen, setCampaignCreatorOpen] = useState(false);
  const [stats, setStats] = useState({
    created: 0,
    scheduled: 0,
    published: 0
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [marketingSubTabValue, setMarketingSubTabValue] = useState(0);
  const [banners, setBanners] = useState<Array<{id: string, title: string, description: string, imageUrl: string}>>([]);
  const [bannerDescription, setBannerDescription] = useState('');
  const [selectedBanner, setSelectedBanner] = useState<{id: string, title: string, description: string, imageUrl: string} | null>(null);
  const [bannerDialogOpen, setBannerDialogOpen] = useState(false);
  const [loadingBanners, setLoadingBanners] = useState(false);
  
  // Add these states at the top with other state declarations
  const [graphicsDialogOpen, setGraphicsDialogOpen] = useState(false);
  const [materialsDialogOpen, setMaterialsDialogOpen] = useState(false);
  
  // Add these states for the different AI generators
  const [showBannerAIGenerator, setShowBannerAIGenerator] = useState(false);
  const [showGraphicsAIGenerator, setShowGraphicsAIGenerator] = useState(false);
  const [showMaterialsAIGenerator, setShowMaterialsAIGenerator] = useState(false);
  
  // Add these states for the generated images for each type
  const [bannerGeneratedImageUrl, setBannerGeneratedImageUrl] = useState<string | null>(null);
  const [graphicsGeneratedImageUrl, setGraphicsGeneratedImageUrl] = useState<string | null>(null);
  const [materialsGeneratedImageUrl, setMaterialsGeneratedImageUrl] = useState<string | null>(null);
  
  // 1. First, simplify the dialog state management
  const [showDialogs, setShowDialogs] = useState({
    banner: false,
    graphics: false,
    materials: false
  });
  
  // Fetch marketing stats for this event
  useEffect(() => {
    const fetchMarketingStats = async () => {
      setLoading(true);
      try {
        // This would be replaced with an actual API call to fetch stats
        // Mock data for now
        setTimeout(() => {
          setStats({
            created: 0,
            scheduled: 0,
            published: 0
          });
          setLoading(false);
        }, 500);
      } catch (err) {
        setError('Failed to load marketing stats');
        setLoading(false);
      }
    };
    
    fetchMarketingStats();
  }, [eventId, organizationId]);
  
  const handleSectionChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveSection(newValue);
  };

  const handleMarketingSubTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setMarketingSubTabValue(newValue);
  };
  
  const fetchBanners = async () => {
    if (!organizationId || !eventId) return;
    setLoadingBanners(true);
    try {
      const bannersRef = collection(db, 'organizations', organizationId, 'events', eventId, 'visualAssets');
      const q = query(bannersRef, where('type', '==', 'banner'));
      const snapshot = await getDocs(q);
      const fetchedBanners = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data() as {title: string, description: string, imageUrl: string}
      }));
      setBanners(fetchedBanners);
    } catch (error) {
      console.error('Error fetching banners:', error);
    } finally {
      setLoadingBanners(false);
    }
  };

  const handleSaveBanner = async (banner: {title: string, description: string, imageUrl: string, id?: string}) => {

    if (!organizationId || !eventId) return;
    try {
       if (banner.id) {
          // Update existing banner

          await updateDoc(
             doc(db, 'organizations', organizationId, 'events', eventId, 'visualAssets', banner.id),
             {
                title: banner.title,
                description: banner.description,
                updatedAt: serverTimestamp(),
             }
          );
       } else {
          // Create new banner

          await addDoc(
             collection(db, 'organizations', organizationId, 'events', eventId, 'visualAssets'),
             {
                title: banner.title,
                description: banner.description,
                imageUrl: banner.imageUrl,
                type: 'banner',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
             }
          );
       }

       fetchBanners();
       setBannerDialogOpen(false);
       setSelectedBanner(null);
    } catch (error) {
       console.error('Error saving banner:', error);
    }
  };

  const handleDeleteBanner = async (bannerId: string) => {
    if (!organizationId || !eventId) return;
    try {
      await deleteDoc(doc(db, 'organizations', organizationId, 'events', eventId, 'visualAssets', bannerId));
      fetchBanners();
    } catch (error) {
      console.error('Error deleting banner:', error);
    }
  };

  useEffect(() => {
    if (marketingSubTabValue === 2 && bannerDialogOpen) {
      fetchBanners();
    }
  }, [marketingSubTabValue, bannerDialogOpen, organizationId, eventId]);
  
  // Update the handler to take a type parameter
  const handleImageGenerated = (
     imageFile: File | null,
     previewUrl: string | null,
     type: 'banner' | 'graphics' | 'materials' = 'banner'
  ) => {
     // Set the correct state based on the type
     if (type === 'banner') {
        setBannerGeneratedImageUrl(previewUrl);
        setShowBannerAIGenerator(false);
        // Update selected banner if editing
        if (selectedBanner) {
           setSelectedBanner({ ...selectedBanner, imageUrl: previewUrl || '' });
        }
     } else if (type === 'graphics') {
        setGraphicsGeneratedImageUrl(previewUrl);
        setShowGraphicsAIGenerator(false);
     } else if (type === 'materials') {
        setMaterialsGeneratedImageUrl(previewUrl);
        setShowMaterialsAIGenerator(false);
     }
  };

  const handleBannerDialogClose = () => {
     setBannerDialogOpen(false);
     if (!selectedBanner) {
        setBannerDescription('');
        setBannerGeneratedImageUrl(null);
     }
  };

  // Update the handleGraphicsDialogClose function
  const handleGraphicsDialogClose = () => {
     setGraphicsDialogOpen(false);
     setGraphicsGeneratedImageUrl(null);
  };

  // Update the handleMaterialsDialogClose function
  const handleMaterialsDialogClose = () => {
     setMaterialsDialogOpen(false);
     setMaterialsGeneratedImageUrl(null);
  };

  const marketingTabContent = (
     <Box>
        <Tabs
           value={marketingSubTabValue}
           onChange={handleMarketingSubTabChange}
           indicatorColor='primary'
           textColor='primary'
           variant='scrollable'
           scrollButtons='auto'
           sx={{ mb: 3 }}
        >
           <Tab label='Social Media Posts' icon={<ChatIcon />} iconPosition='start' />
           <Tab label='Campaigns' icon={<CampaignIcon />} iconPosition='start' />
           <Tab label='Visual Assets' icon={<ImageIcon />} iconPosition='start' />
        </Tabs>

        {marketingSubTabValue === 0 && (
           <Box>
              <Typography variant='h6' gutterBottom>
                 Social Media Posts
              </Typography>
              <Paper sx={{ p: 3, mb: 3 }}>
                 <Box
                    sx={{
                       display: 'flex',
                       justifyContent: 'space-between',
                       alignItems: 'center',
                       mb: 3,
                    }}
                 >
                    <Typography variant='subtitle1'>
                       Create and manage social media posts for this event
                    </Typography>
                    <Button variant='contained' startIcon={<AutoAwesomeIcon />}>
                       Create New Post
                    </Button>
                 </Box>

                 <Alert severity='info'>
                    This section allows you to create individual social media posts for your event.
                    For multi-platform campaigns, use the Campaigns tab.
                 </Alert>
              </Paper>
           </Box>
        )}

        {marketingSubTabValue === 1 && (
           <CampaignsTable
              organizationId={organizationId}
              eventId={eventId}
              eventDetails={{
                 title: eventTitle,
                 description: eventDescription,
                 type: eventType,
                 date: eventDate,
                 location: eventLocation,
              }}
           />
        )}

        {marketingSubTabValue === 2 && (
           <Box>
              <Typography variant='h6' gutterBottom>
                 Visual Assets
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                 <Button
                    variant='contained'
                    startIcon={<AutoAwesomeIcon />}
                    onClick={() => {
                       setSelectedBanner(null);
                       setBannerDescription('');
                       setBannerGeneratedImageUrl(null);
                       setBannerDialogOpen(true);
                    }}
                 >
                    Create New Banner
                 </Button>
              </Box>

              <Box sx={{ mb: 4 }}>
                 <Typography variant='subtitle1' fontWeight={600} gutterBottom>
                    Event Banners
                 </Typography>

                 {loadingBanners ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                       <CircularProgress />
                    </Box>
                 ) : banners.length === 0 ? (
                    <Paper sx={{ p: 3, textAlign: 'center', mb: 3 }}>
                       <ImageIcon
                          sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.5, mb: 2 }}
                       />
                       <Typography variant='h6' gutterBottom>
                          No Banners Created Yet
                       </Typography>
                       <Typography color='text.secondary' sx={{ mb: 3 }}>
                          Create professional banners for your event to use in promotions
                       </Typography>
                       <Button
                          variant='contained'
                          onClick={() => setBannerDialogOpen(true)}
                          startIcon={<AutoAwesomeIcon />}
                       >
                          Create First Banner
                       </Button>
                    </Paper>
                 ) : (
                    <Grid container spacing={2}>
                       {banners.map((banner) => (
                          <Grid item xs={12} md={4} key={banner.id}>
                             <Card sx={{ height: '100%' }}>
                                <CardMedia
                                   component='img'
                                   height={180}
                                   image={banner.imageUrl}
                                   alt={banner.title}
                                />
                                <CardContent>
                                   <Typography variant='h6' noWrap>
                                      {banner.title}
                                   </Typography>
                                   <Typography
                                      variant='body2'
                                      color='text.secondary'
                                      sx={{
                                         overflow: 'hidden',
                                         textOverflow: 'ellipsis',
                                         display: '-webkit-box',
                                         WebkitLineClamp: 2,
                                         WebkitBoxOrient: 'vertical',
                                      }}
                                   >
                                      {banner.description}
                                   </Typography>
                                </CardContent>
                                <CardActions>
                                   <Button
                                      size='small'
                                      onClick={() => {
                                         setSelectedBanner(banner);
                                         setBannerDialogOpen(true);
                                      }}
                                   >
                                      Edit
                                   </Button>
                                   <Button
                                      size='small'
                                      color='error'
                                      onClick={() => handleDeleteBanner(banner.id)}
                                   >
                                      Delete
                                   </Button>
                                </CardActions>
                             </Card>
                          </Grid>
                       ))}
                    </Grid>
                 )}

                 <Box sx={{ mt: 3 }}>
                    <Grid container spacing={2}>
                       <Grid item xs={12} md={4}>
                          <Card>
                             <CardContent>
                                <Typography variant='h6' gutterBottom>
                                   Event Banners
                                </Typography>
                                <Typography variant='body2' color='text.secondary'>
                                   Create banners optimized for different platforms
                                </Typography>
                             </CardContent>
                             <Box sx={{ p: 2, pt: 0 }}>
                                <Button
                                   fullWidth
                                   variant='outlined'
                                   onClick={() => setBannerDialogOpen(true)}
                                >
                                   Create Banners
                                </Button>
                             </Box>
                          </Card>
                       </Grid>
                       <Grid item xs={12} md={4}>
                          <Card>
                             <CardContent>
                                <Typography variant='h6' gutterBottom>
                                   Social Graphics
                                </Typography>
                                <Typography variant='body2' color='text.secondary'>
                                   Generate social media graphics with your event details
                                </Typography>
                             </CardContent>
                             <Box sx={{ p: 2, pt: 0 }}>
                                <Button
                                   fullWidth
                                   variant='outlined'
                                   onClick={() => setGraphicsDialogOpen(true)}
                                >
                                   Create Graphics
                                </Button>
                             </Box>
                          </Card>
                       </Grid>
                       <Grid item xs={12} md={4}>
                          <Card>
                             <CardContent>
                                <Typography variant='h6' gutterBottom>
                                   Promotional Materials
                                </Typography>
                                <Typography variant='body2' color='text.secondary'>
                                   Create flyers and posters for your event
                                </Typography>
                             </CardContent>
                             <Box sx={{ p: 2, pt: 0 }}>
                                <Button
                                   fullWidth
                                   variant='outlined'
                                   onClick={() => setMaterialsDialogOpen(true)}
                                >
                                   Create Materials
                                </Button>
                             </Box>
                          </Card>
                       </Grid>
                    </Grid>
                 </Box>
              </Box>

              {/* Banner Dialog */}
              <Dialog
                 open={bannerDialogOpen}
                 onClose={handleBannerDialogClose}
                 maxWidth='md'
                 fullWidth
              >
                 <DialogTitle>{selectedBanner ? 'Edit Banner' : 'Create New Banner'}</DialogTitle>
                 <DialogContent dividers>
                    <TextField
                       label='Banner Title'
                       fullWidth
                       value={selectedBanner?.title || `${eventTitle} Banner`}
                       margin='normal'
                    />

                    <TextField
                       label='Description'
                       fullWidth
                       multiline
                       rows={3}
                       value={selectedBanner?.description || bannerDescription}
                       onChange={(e) => {
                          if (selectedBanner) {
                             setSelectedBanner({ ...selectedBanner, description: e.target.value });
                          } else {
                             setBannerDescription(e.target.value);
                          }
                       }}
                       margin='normal'
                       placeholder='Add a description for this banner...'
                    />

                    <Box sx={{ my: 3, textAlign: 'center' }}>
                       {selectedBanner?.imageUrl ? (
                          <img
                             src={selectedBanner.imageUrl}
                             alt='Banner Preview'
                             style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }}
                          />
                       ) : bannerGeneratedImageUrl ? (
                          <img
                             src={bannerGeneratedImageUrl}
                             alt='Generated Banner Preview'
                             style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }}
                          />
                       ) : (
                          <Paper
                             sx={{
                                height: 200,
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'grey.100',
                             }}
                          >
                             <Typography color='text.secondary'>
                                Banner preview will appear here
                             </Typography>
                          </Paper>
                       )}
                    </Box>

                    <Button
                       fullWidth
                       variant='contained'
                       color='primary'
                       startIcon={<AutoAwesomeIcon />}
                       sx={{ mt: 2 }}
                       onClick={() => {
                          // Close the banner dialog when opening AI dialog to avoid nesting issues
                          setBannerDialogOpen(false);

                          // Then open the AI generator in the next render cycle
                          setTimeout(() => {
                             setShowBannerAIGenerator(true);
                          }, 100);
                       }}
                    >
                       Generate with AI
                    </Button>
                 </DialogContent>
                 <DialogActions>
                    <Button onClick={handleBannerDialogClose}>Cancel</Button>
                    <Button
                       variant='contained'
                       onClick={() => {
                          if (selectedBanner) {
                             handleSaveBanner(selectedBanner);
                          } else {
                             // Use the generated image if available, otherwise use placeholder
                             handleSaveBanner({
                                title: `${eventTitle} Banner`,
                                description: bannerDescription,
                                imageUrl:
                                   bannerGeneratedImageUrl ||
                                   '/assets/images/placeholder-event.jpg',
                             });
                          }
                       }}
                    >
                       Save Banner
                    </Button>
                 </DialogActions>
              </Dialog>
           </Box>
        )}
     </Box>
  );

  return (
     <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
           <MarketingIcon color='primary' sx={{ mr: 1.5 }} />
           <Typography variant='h5'>
              {activeOrganization?.name || 'Organization'} Event Marketing
           </Typography>
        </Box>

        {/* Main content */}
        {marketingTabContent}

        {/* Grid layout */}
        <Grid container spacing={3}>
           {/* Left sidebar */}
           <Grid item xs={12} md={4}>
              <Box sx={{ mb: 3 }}>
                 <Typography variant='h6' gutterBottom>
                    Marketing Status
                 </Typography>
                 <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip
                       icon={<CheckCircleIcon />}
                       label={`Created: ${stats.created}`}
                       color={stats.created > 0 ? 'primary' : 'default'}
                    />
                    <Chip
                       icon={<ScheduleIcon />}
                       label={`Scheduled: ${stats.scheduled}`}
                       color={stats.scheduled > 0 ? 'primary' : 'default'}
                       variant='outlined'
                    />
                    <Chip
                       icon={<CheckCircleIcon />}
                       label={`Published: ${stats.published}`}
                       color={stats.published > 0 ? 'success' : 'default'}
                    />
                 </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {marketingSubTabValue === 0 && (
                 <Box>
                    <Typography variant='h6' gutterBottom>
                       Generate Social Posts
                    </Typography>
                    <Alert severity='info' sx={{ mb: 2 }}>
                       Use AI to create engaging posts for all your social platforms with just a few
                       clicks.
                    </Alert>
                    <Button
                       variant='contained'
                       fullWidth
                       startIcon={<MarketingIcon />}
                       sx={{ mb: 2 }}
                       onClick={() => setCampaignCreatorOpen(true)}
                    >
                       Create Social Media Campaign
                    </Button>

                    <Box sx={{ mt: 3 }}>
                       <Typography variant='subtitle1' gutterBottom>
                          Available Platforms
                       </Typography>
                       <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          <Chip icon={<FacebookIcon />} label='Facebook' />
                          <Chip icon={<TwitterIcon />} label='Twitter' />
                          <Chip icon={<InstagramIcon />} label='Instagram' />
                          <Chip icon={<LinkedInIcon />} label='LinkedIn' />
                       </Box>
                    </Box>
                 </Box>
              )}

              {marketingSubTabValue === 2 && (
                 <Box>
                    <Typography variant='h6' gutterBottom>
                       Visual Assets
                    </Typography>
                    <Alert severity='info' sx={{ mb: 2 }}>
                       Create on-brand visuals for your event with your organization's logo and
                       branding.
                    </Alert>
                    <Button
                       variant='contained'
                       fullWidth
                       startIcon={<ImageIcon />}
                       sx={{ mb: 2 }}
                       onClick={() => setBannerDialogOpen(true)}
                    >
                       Create Event Banner
                    </Button>

                    <Button
                       variant='outlined'
                       fullWidth
                       startIcon={<UploadIcon />}
                       sx={{ mt: 1 }}
                       onClick={() => setLogoManagerOpen(true)}
                    >
                       Manage Organization Logos
                    </Button>
                 </Box>
              )}
           </Grid>

           {/* Right preview area */}
           <Grid item xs={12} md={8}>
              <Paper
                 variant='outlined'
                 sx={{
                    height: 400,
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: 'column',
                 }}
              >
                 {loading ? (
                    <CircularProgress />
                 ) : (
                    <Box sx={{ textAlign: 'center' }}>
                       <Typography variant='h6'>Marketing Preview</Typography>
                       <Typography variant='body2' color='text.secondary'>
                          Select assets from the tabs above to preview them here.
                       </Typography>
                    </Box>
                 )}
              </Paper>

              {/* Analytics card */}
              <Card sx={{ mt: 3, p: 2 }}>
                 <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                       <AnalyticsIcon color='primary' sx={{ mr: 1 }} />
                       <Typography variant='h6'>Marketing Insights</Typography>
                    </Box>
                    <Typography variant='body2' color='text.secondary'>
                       Once you've created marketing materials, you'll see performance metrics and
                       engagement analytics here.
                    </Typography>
                 </CardContent>
              </Card>
           </Grid>
        </Grid>

        {/* SEPARATE DIALOGS - Don't nest them */}

        {/* Banner AI Generator */}
        <AIImageGenerator
           open={showBannerAIGenerator}
           onClose={() => setShowBannerAIGenerator(false)}
           eventTitle={eventTitle || 'Event'}
           eventDescription={eventDescription || ''}
           eventType={eventType || 'social'}
           onImageGenerated={(imageFile, previewUrl) => {
              // Log detailed information

              // Update state
              setBannerGeneratedImageUrl(previewUrl);

              // Close AI dialog
              setShowBannerAIGenerator(false);

              // Reopen the banner dialog with the new image
              setTimeout(() => {
                 setBannerDialogOpen(true);
              }, 100);
           }}
        />

        {/* Graphics AI Generator */}
        <AIImageGenerator
           open={showGraphicsAIGenerator}
           onClose={() => {
              setShowGraphicsAIGenerator(false);
           }}
           eventTitle={eventTitle || 'Event'}
           eventDescription={eventDescription || ''}
           eventType={eventType || 'social'}
           onImageGenerated={(imageFile: File | null, previewUrl: string | null) => {
              handleImageGenerated(imageFile, previewUrl, 'graphics');
           }}
        />

        {/* Materials AI Generator */}
        <AIImageGenerator
           open={showMaterialsAIGenerator}
           onClose={() => {
              setShowMaterialsAIGenerator(false);
           }}
           eventTitle={eventTitle || 'Event'}
           eventDescription={eventDescription || ''}
           eventType={eventType || 'social'}
           onImageGenerated={(imageFile: File | null, previewUrl: string | null) => {
              handleImageGenerated(imageFile, previewUrl, 'materials');
           }}
        />

        {/* Other dialogs */}
        <OrganizationLogoManager
           open={logoManagerOpen}
           onClose={() => setLogoManagerOpen(false)}
           organizationId={organizationId}
        />

        <SocialMediaCampaignCreator
           open={campaignCreatorOpen}
           onClose={() => setCampaignCreatorOpen(false)}
           organizationId={organizationId}
           eventId={eventId}
           eventDetails={{
              title: eventTitle,
              description: eventDescription,
              type: eventType,
              date: eventDate,
              location: eventLocation,
           }}
        />
     </Paper>
  );
};

export default OrganizationMarketingTab;